import * as React from 'react';
import { cn } from '@/utils/utils';
import { FOCUS } from '@/components/ui/recipes';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface StepperStep {
  id: string;
  label: string;
  /** done: finished; current: the one being worked on; current-done: the one being worked on, with all it needs; todo: still to come. */
  state: 'done' | 'current' | 'current-done' | 'todo';
  /** A step that can't be visited yet. */
  disabled?: boolean;
  /** Why, when it can't be visited. */
  tooltip?: string;
}

interface StepperProps {
  steps: StepperStep[];
  /** Visits a step; without it, the steps only show progress. */
  onSelect?: (id: string) => void;
  className?: string;
}

/** Lureo's stepper: the steps of a flow, how far along it is, and a way back to an earlier step. */
export function Stepper({ steps, onSelect, className }: StepperProps) {
  return (
    <ol className={cn('flex items-center', className)}>
      {steps.map((step, index) => {
        const ticked = step.state === 'done' || step.state === 'current-done';
        const isCurrent = step.state === 'current' || step.state === 'current-done';
        // As production drew them: a 28px circle whose number gives way to a tick that draws in once the step has what it needs
        const circle = (
          <span
            className={cn(
              'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-lureo-border-width-medium text-lureo-12 font-semibold tabular-nums transition-colors duration-lureo-duration-slow',
              step.state === 'done' && 'border-lureo-emphasis bg-lureo-emphasis text-lureo-on-emphasis',
              step.state === 'current' && 'border-lureo-brand-ink bg-lureo-surface text-lureo-brand-ink',
              step.state === 'current-done' && 'border-lureo-emphasis-ink bg-lureo-surface text-lureo-emphasis-ink',
              step.state === 'todo' && 'border-lureo-line-strong text-lureo-ink-subtle',
            )}
          >
            <span className={cn('transition-opacity duration-lureo-duration-fast', ticked ? 'opacity-0' : 'opacity-100')}>{index + 1}</span>
            <svg aria-hidden viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="absolute inset-0 m-auto h-4 w-4">
              <path d="M5 12.5 L10 17 L19 7" pathLength={1} strokeDasharray={1} strokeDashoffset={ticked ? 0 : 1} className="transition-[stroke-dashoffset] duration-lureo-duration-slower ease-lureo-easing-enter" />
            </svg>
          </span>
        );
        // On a phone only the current step keeps its label, so the row fits.
        const label = <span className={cn('text-lureo-13 leading-[1.3]', isCurrent || step.state === 'done' ? 'text-lureo-ink' : 'text-lureo-ink-subtle', !isCurrent && 'hidden sm:inline')}>{step.label}</span>;
        const body = (
          <button
            type="button"
            disabled={!onSelect || step.disabled}
            aria-current={isCurrent ? 'step' : undefined}
            onClick={() => onSelect?.(step.id)}
            className={cn('flex shrink-0 items-center gap-2 rounded-lureo-radius-pill py-0.5 pl-0.5 pr-2 disabled:cursor-default', onSelect && !step.disabled && 'hover:bg-lureo-hover', FOCUS)}
          >
            {circle}
            {label}
          </button>
        );
        return (
          <li key={step.id} className={cn('flex items-center', index < steps.length - 1 && 'flex-1')}>
            {step.tooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">{body}</span>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6}>
                  {step.tooltip}
                </TooltipContent>
              </Tooltip>
            ) : (
              body
            )}
            {index < steps.length - 1 && <span aria-hidden className={cn('mx-3 h-0.5 min-w-4 flex-1 rounded-lureo-radius-pill transition-colors', ticked ? 'bg-lureo-emphasis' : 'bg-lureo-line-strong')} />}
          </li>
        );
      })}
    </ol>
  );
}
